import React, { useEffect, useRef, useState } from "react";
import { AnimationProvider, useAnimation } from "./AnimationContext";
import AnimationsSidebar from "./AnimationsSidebar";
import SlideCanvas from "./SlideCanvas";
import TimelineSequencer from "./TimelineSequencer";
import AnimatePropertiesPanel from "./AnimatePropertiesPanel";
import { Check, Loader2 } from "lucide-react";

const AUTOSAVE_DELAY_MS = 600;

function AnimationEditorInner({ onSave, slideContent, slideImageUrl }) {
  const {
    elements,
    interactions,
    totalDuration,
    canvasBackground,
    canvasBackgroundColor,
    assetFiles,
    getSerializableAnimationData,
  } = useAnimation();

  const [saveStatus, setSaveStatus] = useState("idle"); // idle | pending | saved
  const timeoutRef = useRef(null);
  const skipFirstRef = useRef(true);
  const latestRef = useRef(null);
  latestRef.current = { getSerializableAnimationData, assetFiles, onSave };

  // Debounced autosave whenever the editing state changes.
  // Deliberately excludes onSave/getSerializableAnimationData from deps — they're
  // plain functions recreated every parent render, not actual edits. The ref above
  // always holds the latest version for when the timeout fires.
  useEffect(() => {
    if (skipFirstRef.current) {
      skipFirstRef.current = false;
      return;
    }
    if (!latestRef.current.onSave) return;

    setSaveStatus("pending");
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      const { getSerializableAnimationData, assetFiles, onSave } = latestRef.current;
      onSave(getSerializableAnimationData(), assetFiles);
      timeoutRef.current = null;
      setSaveStatus("saved");
    }, AUTOSAVE_DELAY_MS);

    return () => clearTimeout(timeoutRef.current);
  }, [elements, interactions, totalDuration, canvasBackground, canvasBackgroundColor, assetFiles]);

  // Flush any pending autosave immediately on unmount (e.g. user exits before the debounce fires)
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        const { getSerializableAnimationData, assetFiles, onSave } = latestRef.current;
        onSave?.(getSerializableAnimationData(), assetFiles);
      }
    };
  }, []);

  return (
    <div className="flex flex-col bg-slate-50 rounded-2xl border border-gray-200 overflow-hidden shadow-md mt-6">
      {/* Subheader */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="font-bold text-gray-800 text-sm">Slide Canvas</h2>
          <span className="text-gray-300 text-xs">•</span>
          <span className="text-purpleColor text-xs font-semibold uppercase tracking-wider animate-pulse">
            Animate & Sequencer Mode
          </span>
        </div>

        {onSave && (
          <div className="flex items-center gap-1.5 text-xs font-medium text-gray-400">
            {saveStatus === "pending" ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <Check className="w-3.5 h-3.5 text-emerald-500" />
                {saveStatus === "saved" ? "All changes saved" : "Up to date"}
              </>
            )}
          </div>
        )}
      </div>

      {/* Main Layout */}
      <div className="flex flex-col lg:flex-row ">
        <div className="w-full lg:w-64 h-full shrink-0">
          <AnimationsSidebar />
        </div>
        <div className="flex-1 h-full flex flex-col min-w-0">
          <div className="flex-1 p-4 overflow-y-auto">
            <SlideCanvas slideContent={slideContent} slideImageUrl={slideImageUrl} />
          </div>
          <TimelineSequencer />
        </div>
        <div className="w-full lg:w-72 h-full shrink-0 border-l border-gray-200 bg-white overflow-y-auto">
          <AnimatePropertiesPanel />
        </div>
      </div>
    </div>
  );
}

export default function AnimationEditor({ slideImageUrl = null, onSave = null, initialData = null, initialAssets = [], slideContent = null }) {
  return (
    <AnimationProvider initialBackground={slideImageUrl} initialData={initialData} initialAssets={initialAssets} slideContent={slideContent}>
      <AnimationEditorInner onSave={onSave} slideContent={slideContent} slideImageUrl={slideImageUrl} />
    </AnimationProvider>
  );
}
